(() => {
  const MOBILE_BREAKPOINT = 750;
  const DEFAULT_FPS = 30;
  const DEFAULT_CONFIG = {
    sprites: [],
    columns: 1,
    rows: 1,
    frameWidth: 1,
    frameHeight: 1,
    totalFrames: 1,
    fps: DEFAULT_FPS,
  };

  class YGCanvas2DSpriteRenderer {
    constructor(canvas) {
      this.canvas = canvas;
      this.context = canvas.getContext("2d", { alpha: true });
      this.type = "2d";

      if (!this.context) {
        throw new Error("2d-renderer-unavailable");
      }
    }

    createResource(image) {
      return {
        image,
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      };
    }

    resize(width, height, pixelRatio) {
      const nextWidth = Math.max(Math.round(width * pixelRatio), 1);
      const nextHeight = Math.max(Math.round(height * pixelRatio), 1);

      if (this.canvas.width !== nextWidth || this.canvas.height !== nextHeight) {
        this.canvas.width = nextWidth;
        this.canvas.height = nextHeight;
      }
    }

    clear() {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    renderFrame(resource, frameMetrics) {
      if (!resource || !frameMetrics) {
        this.clear();
        return;
      }

      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.context.drawImage(
        resource.image,
        frameMetrics.sourceX,
        frameMetrics.sourceY,
        frameMetrics.sourceWidth,
        frameMetrics.sourceHeight,
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
    }

    destroy() {}
  }

  class YGWebGLSpriteRenderer {
    constructor(canvas) {
      this.canvas = canvas;
      this.gl =
        canvas.getContext("webgl", {
          alpha: true,
          antialias: true,
          premultipliedAlpha: true,
        }) ||
        canvas.getContext("experimental-webgl", {
          alpha: true,
          antialias: true,
          premultipliedAlpha: true,
        });
      this.type = "webgl";
      this.textures = [];

      if (!this.gl) {
        throw new Error("webgl-renderer-unavailable");
      }

      this.maxTextureSize = this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE) || 0;
      this.program = this.createProgram();
      this.positionLocation = this.gl.getAttribLocation(this.program, "a_position");
      this.texCoordLocation = this.gl.getAttribLocation(this.program, "a_texCoord");
      this.uvRectLocation = this.gl.getUniformLocation(this.program, "u_uvRect");
      this.textureLocation = this.gl.getUniformLocation(this.program, "u_texture");

      this.positionBuffer = this.gl.createBuffer();
      this.texCoordBuffer = this.gl.createBuffer();

      this.gl.useProgram(this.program);
      this.gl.enable(this.gl.BLEND);
      this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
      this.gl.clearColor(0, 0, 0, 0);

      this.bindBuffers();
    }

    createShader(type, source) {
      const shader = this.gl.createShader(type);
      this.gl.shaderSource(shader, source);
      this.gl.compileShader(shader);

      if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
        const error = this.gl.getShaderInfoLog(shader);
        this.gl.deleteShader(shader);
        throw new Error(error || "shader-compile-failed");
      }

      return shader;
    }

    createProgram() {
      const vertexShader = this.createShader(
        this.gl.VERTEX_SHADER,
        `
          attribute vec2 a_position;
          attribute vec2 a_texCoord;
          uniform vec4 u_uvRect;
          varying vec2 v_texCoord;

          void main() {
            gl_Position = vec4(a_position, 0.0, 1.0);
            v_texCoord = mix(u_uvRect.xy, u_uvRect.zw, a_texCoord);
          }
        `
      );
      const fragmentShader = this.createShader(
        this.gl.FRAGMENT_SHADER,
        `
          precision mediump float;
          varying vec2 v_texCoord;
          uniform sampler2D u_texture;

          void main() {
            gl_FragColor = texture2D(u_texture, v_texCoord);
          }
        `
      );
      const program = this.gl.createProgram();

      this.gl.attachShader(program, vertexShader);
      this.gl.attachShader(program, fragmentShader);
      this.gl.linkProgram(program);

      if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
        const error = this.gl.getProgramInfoLog(program);
        this.gl.deleteProgram(program);
        throw new Error(error || "program-link-failed");
      }

      this.gl.deleteShader(vertexShader);
      this.gl.deleteShader(fragmentShader);

      return program;
    }

    bindBuffers() {
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
      this.gl.bufferData(
        this.gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
        this.gl.STATIC_DRAW
      );
      this.gl.enableVertexAttribArray(this.positionLocation);
      this.gl.vertexAttribPointer(this.positionLocation, 2, this.gl.FLOAT, false, 0, 0);

      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), this.gl.STATIC_DRAW);
      this.gl.enableVertexAttribArray(this.texCoordLocation);
      this.gl.vertexAttribPointer(this.texCoordLocation, 2, this.gl.FLOAT, false, 0, 0);
    }

    createResource(image) {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;

      if (width > this.maxTextureSize || height > this.maxTextureSize) {
        throw new Error("texture-too-large");
      }

      const texture = this.gl.createTexture();

      this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
      this.gl.pixelStorei(this.gl.UNPACK_FLIP_Y_WEBGL, true);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, image);

      this.textures.push(texture);

      return {
        texture,
        width,
        height,
      };
    }

    resize(width, height, pixelRatio) {
      const nextWidth = Math.max(Math.round(width * pixelRatio), 1);
      const nextHeight = Math.max(Math.round(height * pixelRatio), 1);

      if (this.canvas.width !== nextWidth || this.canvas.height !== nextHeight) {
        this.canvas.width = nextWidth;
        this.canvas.height = nextHeight;
      }

      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    }

    clear() {
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }

    renderFrame(resource, frameMetrics) {
      if (!resource || !frameMetrics) {
        this.clear();
        return;
      }

      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
      this.gl.useProgram(this.program);
      this.gl.bindTexture(this.gl.TEXTURE_2D, resource.texture);
      this.gl.uniform1i(this.textureLocation, 0);
      this.gl.uniform4f(
        this.uvRectLocation,
        frameMetrics.uvLeft,
        frameMetrics.uvMinY,
        frameMetrics.uvRight,
        frameMetrics.uvMaxY
      );
      this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
    }

    destroy() {
      this.textures.forEach((texture) => {
        this.gl.deleteTexture(texture);
      });
      this.textures = [];

      if (this.positionBuffer) {
        this.gl.deleteBuffer(this.positionBuffer);
      }

      if (this.texCoordBuffer) {
        this.gl.deleteBuffer(this.texCoordBuffer);
      }

      if (this.program) {
        this.gl.deleteProgram(this.program);
      }
    }
  }

  class YGSparkSequence extends HTMLElement {
    static imageCache = new Map();

    constructor() {
      super();
      this.currentMode = "";
      this.currentFrame = 0;
      this.currentPlaybackToken = 0;
      this.isPlaying = false;
      this.hasRevealed = false;
      this.wasCenterActive = false;
      this.wasVisible = false;
      this.measureRafId = 0;
      this.playbackRafId = 0;
      this.playbackStartTime = 0;
      this.playbackStartFrame = 0;
      this.sequenceStates = new Map();
      this.webglDisabledModes = new Set();

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.stepPlayback = this.stepPlayback.bind(this);
    }

    connectedCallback() {
      if (this.hasAttribute("data-yg-spark-bound")) {
        return;
      }

      this.setAttribute("data-yg-spark-bound", "true");

      this.copy = this.querySelector("[data-yg-spark-reveal]");
      this.sequenceBox = this.querySelector("[data-yg-spark-trigger]");
      this.canvas = this.querySelector("[data-yg-spark-canvas]");
      this.mobileQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
      this.reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.desktopConfig = this.parseConfig(this.dataset.desktopConfig);
      this.mobileConfig = this.parseConfig(this.dataset.mobileConfig);
      this.webglCapabilities = this.detectWebGLCapabilities();

      if (!this.sequenceBox || !this.canvas || !this.desktopConfig || !this.mobileConfig) {
        return;
      }

      if (this.reduceMotionQuery.matches) {
        this.revealCopy();
      }

      this.updateMode(true);

      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateCanvasSize();
          this.renderCurrentFrame();
          this.requestMeasure();
        });
        this.resizeObserver.observe(this.sequenceBox);
      }

      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("resize", this.handleResize);
      document.addEventListener("visibilitychange", this.handleVisibilityChange);

      if (typeof this.mobileQuery.addEventListener === "function") {
        this.mobileQuery.addEventListener("change", this.handleResize);
      } else {
        this.mobileQuery.addListener(this.handleResize);
      }

      if (typeof this.reduceMotionQuery.addEventListener === "function") {
        this.reduceMotionQuery.addEventListener("change", this.handleMotionChange);
      } else {
        this.reduceMotionQuery.addListener(this.handleMotionChange);
      }

      this.ensureFirstFrameReady(this.currentMode)
        .then(() => {
          if (!this.isConnected) {
            return;
          }

          this.renderFrame(0);
        })
        .catch((error) => {
          console.error("[yg-spark-sequence] Failed to load the first frame.", error);
        });

      this.requestMeasure();
    }

    disconnectedCallback() {
      this.removeAttribute("data-yg-spark-bound");
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleResize);
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);

      if (typeof this.mobileQuery?.removeEventListener === "function") {
        this.mobileQuery.removeEventListener("change", this.handleResize);
      } else if (typeof this.mobileQuery?.removeListener === "function") {
        this.mobileQuery.removeListener(this.handleResize);
      }

      if (typeof this.reduceMotionQuery?.removeEventListener === "function") {
        this.reduceMotionQuery.removeEventListener("change", this.handleMotionChange);
      } else if (typeof this.reduceMotionQuery?.removeListener === "function") {
        this.reduceMotionQuery.removeListener(this.handleMotionChange);
      }

      this.resizeObserver?.disconnect();
      this.stopPlayback();

      if (this.measureRafId) {
        window.cancelAnimationFrame(this.measureRafId);
        this.measureRafId = 0;
      }

      this.renderer?.destroy();
      this.renderer = null;
      this.sequenceStates.clear();
    }

    detectWebGLCapabilities() {
      const probeCanvas = document.createElement("canvas");
      const gl =
        probeCanvas.getContext("webgl", { alpha: true, antialias: false }) ||
        probeCanvas.getContext("experimental-webgl", { alpha: true, antialias: false });

      if (!gl) {
        return {
          supported: false,
          maxTextureSize: 0,
        };
      }

      return {
        supported: true,
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0,
      };
    }

    parseConfig(rawValue) {
      try {
        const parsedValue = JSON.parse(rawValue || "{}");
        const sprites = Array.isArray(parsedValue.sprites) ? parsedValue.sprites.filter(Boolean) : [];
        const columns = Math.max(Number.parseInt(parsedValue.columns, 10) || DEFAULT_CONFIG.columns, 1);
        const rows = Math.max(Number.parseInt(parsedValue.rows, 10) || DEFAULT_CONFIG.rows, 1);
        const frameWidth = Math.max(
          Number.parseInt(parsedValue.frameWidth, 10) || DEFAULT_CONFIG.frameWidth,
          1
        );
        const frameHeight = Math.max(
          Number.parseInt(parsedValue.frameHeight, 10) || DEFAULT_CONFIG.frameHeight,
          1
        );
        const frameCapacity = Math.max(columns * rows * Math.max(sprites.length, 1), 1);
        const totalFrames = Math.min(
          Math.max(Number.parseInt(parsedValue.totalFrames, 10) || DEFAULT_CONFIG.totalFrames, 1),
          frameCapacity
        );
        const fps = Math.max(Number.parseFloat(parsedValue.fps) || DEFAULT_FPS, 1);

        return {
          sprites,
          columns,
          rows,
          frameWidth,
          frameHeight,
          totalFrames,
          fps,
        };
      } catch (error) {
        console.error("[yg-spark-sequence] Invalid sequence config.", error);
        return null;
      }
    }

    getActiveMode() {
      return this.mobileQuery?.matches ? "mobile" : "desktop";
    }

    getActiveConfig(mode = this.currentMode) {
      return mode === "mobile" ? this.mobileConfig : this.desktopConfig;
    }

    canUseWebGL(config) {
      if (!config || !this.webglCapabilities.supported) {
        return false;
      }

      const maxSheetWidth = config.columns * config.frameWidth;
      const maxSheetHeight = config.rows * config.frameHeight;

      return (
        maxSheetWidth <= this.webglCapabilities.maxTextureSize &&
        maxSheetHeight <= this.webglCapabilities.maxTextureSize
      );
    }

    updateMode(force = false) {
      const nextMode = this.getActiveMode();

      if (!force && nextMode === this.currentMode) {
        return;
      }

      this.currentMode = nextMode;
      this.currentFrame = 0;
      this.stopPlayback();
      this.ensureRendererForMode(nextMode);
      this.updateSequenceAspect();
      this.updateCanvasSize();

      this.ensureFirstFrameReady(nextMode)
        .then(() => {
          if (!this.isConnected || this.currentMode !== nextMode) {
            return;
          }

          this.renderFrame(0);
        })
        .catch((error) => {
          console.error("[yg-spark-sequence] Failed to refresh sequence mode.", error);
        });
    }

    ensureRendererForMode(mode) {
      const config = this.getActiveConfig(mode);
      const nextRendererType =
        !this.webglDisabledModes.has(mode) && this.canUseWebGL(config) ? "webgl" : "2d";

      if (this.renderer && this.renderer.type === nextRendererType) {
        return;
      }

      this.renderer?.destroy();
      this.renderer = nextRendererType === "webgl" ? new YGWebGLSpriteRenderer(this.canvas) : new YGCanvas2DSpriteRenderer(this.canvas);
      this.sequenceStates.clear();
      this.dataset.renderer = this.renderer.type;
    }

    getModeState(mode) {
      if (this.sequenceStates.has(mode)) {
        return this.sequenceStates.get(mode);
      }

      const config = this.getActiveConfig(mode);
      const state = {
        mode,
        config,
        resources: new Array(config.sprites.length),
        resourcePromises: new Array(config.sprites.length),
        allReadyPromise: null,
      };

      this.sequenceStates.set(mode, state);

      return state;
    }

    static loadImage(url, corsMode = "default") {
      if (!url) {
        return Promise.reject(new Error("empty-image-url"));
      }

      const cacheKey = `${corsMode}:${url}`;

      if (!YGSparkSequence.imageCache.has(cacheKey)) {
        YGSparkSequence.imageCache.set(
          cacheKey,
          new Promise((resolve, reject) => {
            const image = new Image();

            if (corsMode === "anonymous") {
              image.crossOrigin = "anonymous";
            }

            image.decoding = "async";
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error(`image-load-failed: ${url}`));
            image.src = url;
          })
        );
      }

      return YGSparkSequence.imageCache.get(cacheKey);
    }

    getImageCorsMode(mode) {
      const config = this.getActiveConfig(mode);
      const canUseWebGLForMode =
        !this.webglDisabledModes.has(mode) && this.canUseWebGL(config);

      return canUseWebGLForMode ? "anonymous" : "default";
    }

    shouldFallbackToCanvas(error, mode) {
      if (this.webglDisabledModes.has(mode)) {
        return false;
      }

      const message = String(error?.message || "");

      return (
        this.getImageCorsMode(mode) === "anonymous" &&
        (error?.name === "SecurityError" ||
          message.includes("cross-origin") ||
          message.includes("texImage2D") ||
          message.includes("image-load-failed"))
      );
    }

    retrySheetWithCanvas(mode, index) {
      const config = this.getActiveConfig(mode);

      if (!config?.sprites[index]) {
        return Promise.reject(new Error(`missing-sprite-url-${mode}-${index}`));
      }

      this.webglDisabledModes.add(mode);
      this.sequenceStates.delete(mode);

      if (this.currentMode === mode) {
        this.ensureRendererForMode(mode);
        this.updateCanvasSize();
      }

      return YGSparkSequence.loadImage(config.sprites[index]).then((image) => {
        if (!this.renderer || (this.currentMode === mode && this.renderer.type !== "2d")) {
          this.ensureRendererForMode(mode);
          this.updateCanvasSize();
        }

        const fallbackState = this.getModeState(mode);
        const resource = this.renderer.createResource(image);
        fallbackState.resources[index] = resource;

        return resource;
      });
    }

    ensureSheetReady(mode, index) {
      const state = this.getModeState(mode);

      if (!state.config.sprites[index]) {
        return Promise.reject(new Error(`missing-sprite-url-${mode}-${index}`));
      }

      if (!state.resourcePromises[index]) {
        const spriteUrl = state.config.sprites[index];
        const corsMode = this.getImageCorsMode(mode);

        state.resourcePromises[index] = YGSparkSequence.loadImage(spriteUrl, corsMode)
          .then((image) => {
            if (!this.renderer) {
              throw new Error("renderer-not-ready");
            }

            const resource = this.renderer.createResource(image);
            state.resources[index] = resource;

            return resource;
          })
          .catch((error) => {
            if (this.shouldFallbackToCanvas(error, mode)) {
              console.warn(
                "[yg-spark-sequence] WebGL sprite upload blocked, falling back to Canvas 2D.",
                error
              );
              return this.retrySheetWithCanvas(mode, index);
            }

            throw error;
          });
      }

      return state.resourcePromises[index];
    }

    ensureFirstFrameReady(mode) {
      return this.ensureSheetReady(mode, 0);
    }

    ensureAllSheetsReady(mode) {
      const state = this.getModeState(mode);

      if (!state.allReadyPromise) {
        state.allReadyPromise = Promise.all(
          state.config.sprites.map((_, index) => this.ensureSheetReady(mode, index))
        );
      }

      return state.allReadyPromise;
    }

    updateSequenceAspect() {
      const config = this.getActiveConfig();

      if (!config) {
        return;
      }

      this.sequenceBox?.style.setProperty(
        "--yg-spark-sequence-aspect",
        `${config.frameWidth} / ${config.frameHeight}`
      );
    }

    updateCanvasSize() {
      if (!this.renderer || !this.sequenceBox) {
        return;
      }

      const width = this.sequenceBox.clientWidth;
      const height = this.sequenceBox.clientHeight;

      if (!width || !height) {
        return;
      }

      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      this.renderer.resize(width, height, pixelRatio);
    }

    handleScroll() {
      this.requestMeasure();
    }

    handleResize() {
      this.updateMode();
      this.requestMeasure();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopPlayback();
        return;
      }

      this.requestMeasure();
    }

    handleMotionChange() {
      if (this.reduceMotionQuery.matches) {
        this.stopPlayback();
        this.revealCopy();
        this.renderFrame(0);
        return;
      }

      this.requestMeasure();
    }

    requestMeasure() {
      if (this.measureRafId) {
        return;
      }

      this.measureRafId = window.requestAnimationFrame(() => {
        this.measureRafId = 0;
        this.evaluateViewportState();
      });
    }

    evaluateViewportState() {
      if (!this.sequenceBox) {
        return;
      }

      this.updateMode();
      this.updateCanvasSize();

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportCenter = viewportHeight * 0.5;
      const triggerRect = this.sequenceBox.getBoundingClientRect();
      const isVisible = triggerRect.bottom > 0 && triggerRect.top < viewportHeight;
      const isCenterActive = triggerRect.top <= viewportCenter && triggerRect.bottom >= viewportCenter;

      if (this.copy && !this.hasRevealed && this.isCrossingViewportCenter(this.copy.getBoundingClientRect(), viewportCenter)) {
        this.revealCopy();
      }

      if (isVisible) {
        this.ensureAllSheetsReady(this.currentMode).catch((error) => {
          console.error("[yg-spark-sequence] Failed to preload sprite sheets.", error);
        });
      }

      if (isCenterActive && !this.wasCenterActive && !this.reduceMotionQuery.matches) {
        this.restartPlayback();
      }

      if (!isVisible && this.isPlaying) {
        this.stopPlayback();
      }

      this.wasVisible = isVisible;
      this.wasCenterActive = isCenterActive;
    }

    isCrossingViewportCenter(rect, viewportCenter) {
      return rect.top <= viewportCenter && rect.bottom >= viewportCenter;
    }

    revealCopy() {
      if (!this.copy) {
        return;
      }

      this.hasRevealed = true;
      this.copy.classList.add("is-visible");
    }

    restartPlayback() {
      this.stopPlayback(true);

      const mode = this.currentMode;
      const playbackToken = ++this.currentPlaybackToken;

      this.currentFrame = 0;
      this.renderFrame(0);

      this.ensureAllSheetsReady(mode)
        .then(() => {
          if (!this.isConnected || this.currentMode !== mode || playbackToken !== this.currentPlaybackToken) {
            return;
          }

          this.isPlaying = true;
          this.playbackStartTime = 0;
          this.playbackStartFrame = 0;
          this.playbackRafId = window.requestAnimationFrame(this.stepPlayback);
        })
        .catch((error) => {
          console.error("[yg-spark-sequence] Failed to start playback.", error);
        });
    }

    stopPlayback(preserveRequest = false) {
      if (!preserveRequest) {
        this.currentPlaybackToken += 1;
      }

      this.isPlaying = false;
      this.playbackStartTime = 0;

      if (this.playbackRafId) {
        window.cancelAnimationFrame(this.playbackRafId);
        this.playbackRafId = 0;
      }
    }

    stepPlayback(timestamp) {
      if (!this.isPlaying) {
        return;
      }

      const config = this.getActiveConfig();

      if (!config) {
        this.stopPlayback();
        return;
      }

      if (!this.playbackStartTime) {
        this.playbackStartTime = timestamp;
      }

      const elapsed = Math.max(timestamp - this.playbackStartTime, 0);
      const nextFrame = Math.min(
        config.totalFrames - 1,
        this.playbackStartFrame + Math.floor((elapsed / 1000) * config.fps)
      );

      if (nextFrame !== this.currentFrame) {
        this.currentFrame = nextFrame;
        this.renderCurrentFrame();
      }

      if (nextFrame >= config.totalFrames - 1) {
        this.stopPlayback();
        return;
      }

      this.playbackRafId = window.requestAnimationFrame(this.stepPlayback);
    }

    getFrameMetrics(config, resource, frameIndex) {
      if (!config || !resource) {
        return null;
      }

      const clampedFrame = Math.max(Math.min(frameIndex, config.totalFrames - 1), 0);
      const sheetCapacity = config.columns * config.rows;
      const sheetIndex = Math.min(
        Math.floor(clampedFrame / sheetCapacity),
        Math.max(config.sprites.length - 1, 0)
      );
      const localFrame = clampedFrame - sheetIndex * sheetCapacity;
      const columnIndex = localFrame % config.columns;
      const rowIndex = Math.floor(localFrame / config.columns);
      const sourceX = columnIndex * config.frameWidth;
      const sourceY = rowIndex * config.frameHeight;
      const sourceWidth = config.frameWidth;
      const sourceHeight = config.frameHeight;
      const uvInsetX = 0.5 / resource.width;
      const uvInsetY = 0.5 / resource.height;
      const uvLeft = sourceX / resource.width + uvInsetX;
      const uvRight = (sourceX + sourceWidth) / resource.width - uvInsetX;
      // flipY is enabled when uploading sprite sheets, so convert top-origin
      // image coordinates into bottom-origin texture coordinates here.
      const uvMinY = 1 - (sourceY + sourceHeight) / resource.height + uvInsetY;
      const uvMaxY = 1 - sourceY / resource.height - uvInsetY;

      return {
        sheetIndex,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        uvLeft,
        uvRight,
        uvMinY,
        uvMaxY,
      };
    }

    renderCurrentFrame() {
      this.renderFrame(this.currentFrame);
    }

    renderFrame(frameIndex) {
      const state = this.getModeState(this.currentMode);
      const config = state?.config;

      if (!config) {
        return;
      }

      const sheetCapacity = config.columns * config.rows;
      const clampedFrame = Math.max(Math.min(frameIndex, config.totalFrames - 1), 0);
      const sheetIndex = Math.min(
        Math.floor(clampedFrame / sheetCapacity),
        Math.max(config.sprites.length - 1, 0)
      );
      const resource = state.resources[sheetIndex];

      if (!resource) {
        return;
      }

      const frameMetrics = this.getFrameMetrics(config, resource, clampedFrame);

      this.updateCanvasSize();
      this.renderer.renderFrame(resource, frameMetrics);
      this.setAttribute("data-yg-spark-ready", "true");
    }
  }

  if (!window.customElements.get("yg-spark-sequence")) {
    window.customElements.define("yg-spark-sequence", YGSparkSequence);
  }
})();
